/**
 * GLYDE Voice SDK - TextChat Component
 * 
 * Full text chat UI component with message display, input field,
 * and loading indicators.
 * 
 * @example
 * <TextChat
 *   publishableKey="pk_..."
 *   contextId="application-uuid"
 *   unityApiUrl="https://api.glydeunity.com"
 * />
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTextChat } from '../hooks/useTextChat';
import type { ChatMessage, VoiceContextType } from '../core/types';
import { SendIcon, UserIcon, BotIcon } from '../ui/icons';
import { componentStyles } from '../ui/styles';

/**
 * TextChat component props
 */
export interface TextChatProps {
  /** Context identifier (format depends on context type) */
  contextId: string;
  /** Voice/text context type used by Unity prompt routing */
  contextType?: VoiceContextType;
  /** Publishable key for authentication */
  publishableKey?: string;
  /** API key for authentication */
  apiKey?: string;
  /** JWT auth token for GLYDEBuddy passthrough */
  authToken?: string;
  /** Unity API URL */
  unityApiUrl?: string;
  /** Fixed height for the component */
  height?: string | number;
  /** Placeholder text for input */
  placeholder?: string;
  /** Auto-initialize chat on mount */
  autoInit?: boolean;
  /** Callback when message is sent */
  onMessage?: (message: ChatMessage) => void;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
  /** Callback when context switches (e.g. screening -> candidate_discover) */
  onContextSwitch?: (contextType: string, contextId: string) => void;
}

/**
 * TextChat Component
 * 
 * Provides a complete text chat interface with:
 * - Message history display
 * - Text input with send button
 * - Loading indicators
 * - Auto-scroll to latest messages
 */
export const TextChat: React.FC<TextChatProps> = ({
  contextId,
  contextType = 'discovery',
  publishableKey,
  apiKey,
  authToken,
  unityApiUrl = 'https://api.glydeunity.com',
  height = '600px',
  placeholder = 'Type your message...',
  autoInit = true,
  onMessage,
  onError,
  onContextSwitch,
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    status,
    messages,
    isLoading,
    errorMessage,
    agentMetadata,
    sendMessage,
  } = useTextChat({
    publishableKey,
    apiKey,
    authToken,
    contextType,
    contextId,
    unityBaseUrl: unityApiUrl,
    autoInit,
    onContextSwitch: onContextSwitch
      ? (payload) => onContextSwitch(payload.contextType, payload.contextId)
      : undefined,
  });

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Notify parent of errors
  useEffect(() => {
    if (status === 'error' && errorMessage && onError) {
      onError(new Error(errorMessage));
    }
  }, [status, errorMessage, onError]);

  /**
   * Derive a display name from server metadata or context type
   */
  const agentDisplayName = agentMetadata?.agent_name
    || agentMetadata?.company_name
    || getDefaultAgentName(contextType);

  /**
   * Handle send message
   */
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const messageText = inputValue.trim();
    setInputValue('');
    
    await sendMessage(messageText);
    
    if (onMessage) {
      onMessage({ role: 'user', content: messageText });
    }

    // Return focus to the input for the next message
    inputRef.current?.focus();
  };

  /**
   * Handle key press (Enter to send)
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ 
      background: 'var(--glyde-background, white)', 
      borderRadius: '12px', 
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      height: typeof height === 'number' ? `${height}px` : height,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Messages Area */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        background: 'var(--glyde-background, white)',
      }}>
        {status === 'loading' && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--glyde-text-muted, #94a3b8)', padding: '20px' }}>
            <div className="glyde-spinner" style={{ margin: '0 auto 12px' }} />
            Loading conversation...
          </div>
        )}

        {messages.map((msg, idx) => (
          <ChatMessageBubble key={idx} message={msg} agentName={agentDisplayName} />
        ))}
        
        {isLoading && (
          <div style={{ color: 'var(--glyde-text-muted, #94a3b8)', fontSize: '0.9rem', marginLeft: '42px' }}>
            <TypingIndicator />
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glyde-border, #eee)' }}>
        {errorMessage && (
          <p style={{ 
            color: 'var(--glyde-error, #ef4444)', 
            fontSize: '0.85rem', 
            marginBottom: '10px' 
          }}>
            {errorMessage}
          </p>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{ 
              ...componentStyles.inputField,
              flex: 1,
            }}
            disabled={isLoading}
          />
          <button 
            onClick={handleSend}
            disabled={isLoading || !inputValue.trim()}
            style={{ 
              ...componentStyles.iconButton,
              background: 'var(--glyde-secondary, #0284c7)', 
              width: '48px',
              height: '48px',
              opacity: isLoading || !inputValue.trim() ? 0.5 : 1,
              cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            <SendIcon size={20} color="white" />
          </button>
        </div>
      </div>

      {/* Inline styles */}
      <style>{`
        @keyframes typing-dot {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/**
 * Get a default agent name based on context type
 */
function getDefaultAgentName(contextType: VoiceContextType): string {
  switch (contextType) {
    case 'recruiter': return 'AI Copilot';
    case 'screening': return 'AI Recruiter';
    case 'discovery':
    case 'job_apply':
    case 'candidate_discover':
    case 'candidate_apply':
      return 'AI Assistant';
    default: return 'AI Assistant';
  }
}

/**
 * Chat message bubble component
 * Only displays user and assistant messages - system messages are never shown
 */
const ChatMessageBubble: React.FC<{ message: ChatMessage; agentName: string }> = ({ message, agentName }) => {
  const isUser = message.role === 'user';
  
  if (message.role === 'system') {
    return null;
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      gap: '10px', 
      flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-start'
    }}>
      {/* Avatar */}
      <div style={{ 
        width: '32px', 
        height: '32px', 
        borderRadius: '50%', 
        background: isUser ? 'var(--glyde-primary-light, #e0e7ff)' : 'var(--glyde-secondary-light, #f0f9ff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {isUser 
          ? <UserIcon size={18} color="var(--glyde-primary, #4338ca)" /> 
          : <BotIcon size={18} color="var(--glyde-secondary, #0284c7)" />
        }
      </div>
      
      {/* Message */}
      <div style={{ maxWidth: '70%' }}>
        {!isUser && (
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--glyde-text-muted, #94a3b8)',
            marginBottom: '4px',
            marginLeft: '4px',
          }}>
            {agentName}
          </div>
        )}
        <div style={{ 
          padding: '12px 16px', 
          borderRadius: '12px', 
          background: isUser ? 'var(--glyde-primary, #4338ca)' : 'var(--glyde-surface, #f1f5f9)',
          color: isUser ? 'white' : 'var(--glyde-text, #1e293b)',
          borderTopRightRadius: isUser ? '4px' : '12px',
          borderTopLeftRadius: isUser ? '12px' : '4px',
          lineHeight: 1.5,
        }}>
          {message.content}
        </div>
      </div>
    </div>
  );
};

/**
 * Typing indicator component
 */
const TypingIndicator: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
    <span>Typing</span>
    <span style={{ display: 'flex', gap: '2px' }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: 'var(--glyde-text-muted, #94a3b8)',
            animation: `typing-dot 1.4s infinite`,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </span>
  </div>
);

export default TextChat;
